import { useEffect } from "react";
import "./DataestatesummaryTab.scss";
import { useDispatch, useSelector } from "react-redux";
import DOMPurify from "dompurify";
import {
  Explore as ExploreIcon,
  Public as PublicIcon,
  Person as PersonIcon,
  VerifiedUser as VerifiedUserIcon,
  History as HistoryIcon,
  Assignment as AssignmentIcon,
  ArrowForward as ArrowForwardIcon,
} from "@material-ui/icons";
import { FETCH_DATA_ESTATE_SUMMARY_TAB } from "../DataTagConstants";
import Page_Loader from "../../../assets/vizr_images/page_loader.gif";
import { numberFormatter } from "../../../components/common/Dashboard/utils";

const HEAD_ICON_MAP = {
  DATA_OWNERSHIP: PersonIcon,
  EXPOSURE_RISK: PublicIcon,
  RISK_COMPLIANCE_OVERSIGHT: VerifiedUserIcon,
  ARCHIVAL_RETENTION_STRATEGY: HistoryIcon,
};

function highlightHtml(raw) {
  if (typeof raw !== "string") return "-";

  const NUMBER_PATTERN = /\d{1,3}(?:,\d{3})*(?:\.\d+)?%?/g;
  let updated = raw.replace(NUMBER_PATTERN, (match) => {
    if (match.endsWith("%")) {
      return `<b>${match}</b>`;
    }
    const numericValue = Number(match.replace(/,/g, ""));
    const formatted = Number.isNaN(numericValue)
      ? match
      : numberFormatter(numericValue);
    return `<b>${formatted}</b>`;
  });
  updated = updated.replace(/\n/g, "<br />");

  return DOMPurify.sanitize(updated);
}

// The backend already wraps numbers in <b> for recommended-action steps,
// regex (highlightHtml) on top would double-process text that's already
// marked up and risk nested <b><b>...</b></b> tags.
function sanitizePreFormatted(raw) {
  if (typeof raw !== "string") return "-";
  return DOMPurify.sanitize(raw.replace(/\n/g, "<br />"));
}
function formatHead(head = "") {
  return head
    .toLowerCase()
    .split("_")
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
}

function formatMetricValue(value) {
  if (
    value === null ||
    value === undefined ||
    value === "null" ||
    value === ""
  ) {
    return "—";
  }
  if (typeof value === "string" && value.trim().endsWith("%")) {
    return value;
  }
  const numericValue = Number(String(value).replace(/,/g, ""));
  return Number.isNaN(numericValue) ? value : numberFormatter(numericValue);
}

export default function DataEstateSummaryTab() {
  const dispatch = useDispatch();

  const dataEstateSummaryTabLoader = useSelector(
    (state) => state.dataTagReducer?.dataEstateSummaryTabLoader,
  );
  const dataEstateSummaryTab = useSelector(
    (state) => state.dataTagReducer?.dataEstateSummaryTab,
  );

  useEffect(() => {
    dispatch({ type: FETCH_DATA_ESTATE_SUMMARY_TAB });
  }, [dispatch]);

  const narrativePoints = dataEstateSummaryTab?.narrativePoints || [];
  const hardeningItems = dataEstateSummaryTab?.hardeningItems || [];

  if (dataEstateSummaryTabLoader) {
    return null;
  }

  const quadrants = narrativePoints.map((point) => {
    const action = hardeningItems.find((item) => item.head === point.head);
    return {
      head: point.head,
      narrativeText: point.text,
      subPoints: point.subPoints || [],
      severity: action?.severity,
      actionText: action?.text,
      subItems: action?.subItems || [],
      metrics: action?.metrics || [],
    };
  });

  return (
    <>
      {dataEstateSummaryTabLoader && (
        <img
          src={Page_Loader}
          alt="loading"
          className="loader-data-source dataTag"
        ></img>
      )}
      <div className="data-estate-summary">
        <div className="des-section">
          <div className="des-section-label">
            <span className="des-section-label-bar"></span>
            <ExploreIcon
              className="des-section-label-icon"
              style={{ fontSize: 16 }}
            />
            Key Insights &amp; Recommendations
          </div>

          <p className="des-insights-intro">
            Based on observations from the Druvstar DataVision platform, the
            following are key insights into your organization&rsquo;s data
            estate from a data governance and compliance perspective.
          </p>

          <div className="des-quadrant-grid">
            {quadrants.map((q) => {
              const Icon = HEAD_ICON_MAP[q.head] || AssignmentIcon;
              const severityClass = `sev-${(q.severity || "medium").toLowerCase()}`;

              return (
                <div className="des-quadrant-card" key={q.head}>
                  <div className="des-quadrant-header">
                    <span className={`des-quadrant-icon-wrap ${severityClass}`}>
                      <Icon style={{ fontSize: 18 }} />
                    </span>
                    <span className="des-quadrant-title">{formatHead(q.head)}</span>
                    {q.severity && (
                      <span className={`des-rec-sev-badge ${severityClass}`}>
                        {q.severity.toUpperCase()}
                      </span>
                    )}
                  </div>

                  <div className="des-quadrant-body">
                    <p
                      className="des-quadrant-text"
                      dangerouslySetInnerHTML={{
                        __html: highlightHtml(q.narrativeText),
                      }}
                    />

                    {q.subPoints.length > 0 && (
                      <ul className="des-row-subpoints">
                        {q.subPoints.map((subPoint, subIndex) => (
                          <li
                            key={subIndex}
                            dangerouslySetInnerHTML={{
                              __html: highlightHtml(subPoint),
                            }}
                          />
                        ))}
                      </ul>
                    )}

                    {q.metrics.length > 0 && (
                      <span className="des-action-chips">
                        {q.metrics.map((m) => (
                          <span className="des-action-chip" key={m.label}>
                            <strong>{formatMetricValue(m.value)}</strong>
                            &nbsp;{m.label}
                          </span>
                        ))}
                      </span>
                    )}

                    {q.subItems.length > 0 && (
                      <div className="des-rec-action-callout">
                        <div className="des-rec-action-label">
                          <ArrowForwardIcon style={{ fontSize: 12 }} />
                          Recommended Action{q.subItems.length > 1 ? "s" : ""}
                        </div>
                        <ul className="des-rec-action-list">
                          {q.subItems.map((step, stepIndex) => (
                            <li
                              key={stepIndex}
                              dangerouslySetInnerHTML={{
                                __html: sanitizePreFormatted(step),
                              }}
                            />
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </>
  );
}